/** @jsxImportSource @emotion/react */

import { ThemeProvider } from "@bonusx-global/bx-design-system";
import type { Interpolation, Theme } from "@emotion/react";
import { css } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import Grid from "@mui/material/Grid";
import StyledEngineProvider from "@mui/material/StyledEngineProvider";
import { ErrorBoundary } from "react-error-boundary";
import { containerWidthCollection, headerHeight } from "../../R";
import { NODE_ENV } from "../../constants/env";
import { MOBILE_CHILDREN_CONTAINER_SIDEBAR_PADDING } from "../../constants/layouts/layout.const";
import useIsMobile from "../../hooks/device/useIsMobile";
import { BoxCentered } from "../../legacy/components/BoxCentered";
import MobileAppBannerWrapper from "../../legacy/components/MobileAppBanner/mobile-app-banner-wrapper";
import { EmptyComponent } from "../../legacy/components/common/EmptyComponent";
import { Modal } from "../../legacy/components/poppers/Modal";
import citizenTheme from "../../legacy/styles/citizenTheme";
import { attemptChunkLoadRecovery } from "../../lib/chunk-load-recovery/attempt-chunk-load-recovery";

interface BaseLayoutProps {
  header?: JSX.Element;
  children?: any;
  additionalCss?: Interpolation<Theme>;
}

export function BaseLayout({ header, children, additionalCss }: Readonly<BaseLayoutProps>) {
  const isMobile = useIsMobile();

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={citizenTheme}>
        <CssBaseline />
        <Modal />
        <ErrorBoundary
          FallbackComponent={EmptyComponent}
          onError={(e) => {
            if (NODE_ENV === "development") {
              console.error(e);
              return;
            }
            if (attemptChunkLoadRecovery(e)) {
              return;
            }
            window.location.href = "/unexpected-error.html";
          }}
        >
          <Grid container css={{ minHeight: "100vh", ...(additionalCss as object) }}>
            <Grid item xs={12}>
              <MobileAppBannerWrapper />
              {header}
              <BoxCentered
                css={{
                  height: isMobile ? `calc(100% - ${headerHeight})` : "initial",
                  padding: isMobile ? MOBILE_CHILDREN_CONTAINER_SIDEBAR_PADDING : "inherit",
                }}
              >
                <Grid
                  container
                  css={css`
                    display: block;
                    height: 100%;
                    max-width: ${isMobile ? "100vw" : containerWidthCollection.get("default")};
                  `}
                >
                  <Grid item xs={12} css={{ height: "100%" }}>
                    {children}
                  </Grid>
                </Grid>
              </BoxCentered>
            </Grid>
          </Grid>
        </ErrorBoundary>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
